import { IS_AQUEDUCT } from '$lib/site';
import type { Locale } from '$lib/localization/locale';
import { PUBLIC_DIFFUSION_API_ENDPOINT } from '$env/static/public';
import type { User } from 'firebase/auth';

type AuthEmailResponse = {
	message: string;
};

type ApiErrorResponse = {
	detail?: string;
};

export class AuthEmailRequestError extends Error {
	readonly status: number;

	constructor(status: number, message: string) {
		super(message);
		this.name = 'AuthEmailRequestError';
		this.status = status;
	}
}

async function readResponse(response: Response): Promise<AuthEmailResponse> {
	if (response.ok) {
		return (await response.json()) as AuthEmailResponse;
	}

	let message: string = 'The authentication email could not be requested.';
	try {
		const body: ApiErrorResponse = (await response.json()) as ApiErrorResponse;
		if (body.detail) message = body.detail;
	} catch {
		// Keep the generic message when the API did not return JSON.
	}
	throw new AuthEmailRequestError(response.status, message);
}

export async function requestVerificationEmail(user: User, locale: Locale = 'en'): Promise<string> {
	const token: string = await user.getIdToken(true);
	const response: Response = await fetch(
		`${PUBLIC_DIFFUSION_API_ENDPOINT}/auth/email/verification`,
		{
			method: 'POST',
			headers: {
				Authorization: `Bearer ${token}`,
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ locale: IS_AQUEDUCT ? locale : 'en' })
		}
	);
	return (await readResponse(response)).message;
}

export async function requestPasswordResetEmail(
	email: string,
	locale: Locale = 'en'
): Promise<string> {
	const response: Response = await fetch(
		`${PUBLIC_DIFFUSION_API_ENDPOINT}/auth/email/password-reset`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ email, locale: IS_AQUEDUCT ? locale : 'en' })
		}
	);
	return (await readResponse(response)).message;
}
